import React from "react";
import { useSound } from "../../utils/sound/SoundProvider";
import useGameBase from '../../hooks/useGameBase';

function TicTacToeGame({ gameState, gamePlayers, user, onGameAction, isHost, onPlayAgain, socket, roomId }) {
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  // Helper to get winning line indices (assume gameState.winningLine is an array of indices if there is a winner)
  // winningLine: array of indices for the winning squares
  let winningLine = gameState.winningLine || [];
  // If winner exists but no winningLine, calculate it client-side (for legacy server)
  if (gameState.winner && winningLine.length !== 3 && gameState.winner !== 'draw') {
    const winPatterns = [
      [0,1,2], [3,4,5], [6,7,8],
      [0,3,6], [1,4,7], [2,5,8],
      [0,4,8], [2,4,6]
    ];
    for (let pattern of winPatterns) {
      if (pattern.every(i => gameState.board[i] === gameState.winner)) {
        winningLine = pattern;
        break;
      }
    }
  }
  // sound: play positive when a winner (non-draw) appears
  const sound = useSound();
  const playedForWinnerRef = React.useRef(null);
  React.useEffect(() => {
    if (gameState.winner && gameState.winner !== 'draw') {
      // play only once per winner id/value
      const key = `${gameState.winner}-${gameState.winningLine?.join(',') || ''}`;
      if (playedForWinnerRef.current !== key) {
        try { sound && sound.play && sound.play('ui:positive'); } catch (e) {}
        playedForWinnerRef.current = key;
      }
    } else {
      // reset on draw or no winner
      playedForWinnerRef.current = null;
    }
  }, [gameState.winner, gameState.winningLine, sound]);
  // ...existing code...
    
  return (
    <div className="mt-4">
      <style>{`
        @keyframes pulseTicTac { 0% { transform: scale(1); } 50% { transform: scale(1.12); } 100% { transform: scale(1); } }
        .tictactoe-pulse { animation: pulseTicTac 800ms ease-in-out infinite; }
      `}</style>
      {/* Use explicit 3 rows and fixed height so rows and columns share equal spacing */}
      <div className="relative grid grid-cols-3 grid-rows-3 gap-1 w-48 h-48 mx-auto">
        {gameState.board.map((cell, i) => {
          const isDisabled =
            cell ||
            gameState.winner ||
            gameState.currentPlayer !== user.socketId;
          return (
            <button
              key={i}
              className={`border w-full h-full flex items-center justify-center text-2xl transition-colors duration-150
                ${winningLine.includes(i) ? (gameState.showWinningState ? 'bg-green-100 dark:bg-green-900 tictactoe-pulse' : 'bg-green-100 dark:bg-green-900') : 'bg-white dark:bg-gray-800'}
                border-gray-300 dark:border-gray-600
                hover:bg-blue-50 dark:hover:bg-gray-700
                disabled:opacity-60`}
              style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
              onClick={() => submitAction(i).catch(()=>{})}
              disabled={isDisabled}
            >
              {cell && (
                <span className={cell === 'X' ? 'text-blue-600 dark:text-blue-300 font-bold' : cell === 'O' ? 'text-pink-600 dark:text-pink-300 font-bold' : ''}>
                  {cell}
                </span>
              )}
            </button>
          );
        })}
        {/* Draw green line for winning squares if winner and winningLine exists */}
        {gameState.winner && winningLine.length === 3 && (
          <svg className="absolute top-0 left-0 w-full h-full pointer-events-none z-10" viewBox="0 0 192 192">
            {/* Calculate line coordinates based on winningLine */}
            {(() => {
              // Map cell index to center coordinates
              const cellCoords = [
                [32, 32], [96, 32], [160, 32],
                [32, 96], [96, 96], [160, 96],
                [32, 160], [96, 160], [160, 160]
              ];
              const [start, , end] = winningLine.map(idx => cellCoords[idx]);
              // Draw line from start to end
              return <line x1={start[0]} y1={start[1]} x2={end[0]} y2={end[1]} stroke="green" strokeWidth="10" strokeLinecap="round" opacity={0.2} />;
            })()}
          </svg>
        )}
      </div>
      {/* Player symbol legend */}
      <div className="flex justify-center gap-6 mt-4 mb-2">
        {gamePlayers.slice(0, 2).map((p, idx) => (
          <div key={p.socketId} className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 rounded px-3 py-1 shadow-sm">
            <span style={{ backgroundColor: p.color || '#e57373', borderRadius: '4px', padding: '2px 6px', display: 'inline-block' }}>{p.icon || '😀'}</span>
            <span className="font-semibold text-gray-900 dark:text-gray-100">{p.username}</span>
            <span className={idx === 0 ? "text-blue-600 dark:text-blue-300 font-mono font-bold" : "text-pink-600 dark:text-pink-300 font-mono font-bold"}>
              {idx === 0 ? 'X' : 'O'}
            </span>
          </div>
        ))}
      </div>

    </div>
  );
}

TicTacToeGame.Guide = function TicTacToeGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">How to Play Tic Tac Toe</h3>
      <ul className="list-disc ml-6">
        <li>Take turns placing X or O on the board.</li>
        <li>First to get three in a row (horizontally, vertically, or diagonally) wins.</li>
        <li>If the board fills with no winner, it's a draw.</li>
      </ul>
    </div>
  );
};

export default TicTacToeGame;
